/**
 * HomeScreen - Main landing page with in-place conversation
 *
 * Version: 2.1.0 - Force redeploy to fix navigation bug
 * Build: 2026-01-31
 *
 * IMPORTANT: Messages should display IN-PLACE on this screen.
 * DO NOT navigate to /chat - that's the deprecated interface.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useGhost } from '../contexts/GhostContext';
import { AuthModal } from '../components/AuthModal';
import { sendMessageStream, StreamChunk, IntentResponse, ExecutionMode } from '../services/api';
// parseOptions REMOVED (2026-02-19): Cards now come from Synapse ViewRecipe (DV2), not frontend parsing
// import { OptionsGrid } from '../components/OptionCard';
// import { parseOptionsFromResponse, hasSelectableOptions } from '../utils/parseOptions';
import { createConversation, addMessage as addMessageToFirestore, getMessages, getConversation } from '../services/conversations';
// createTask removed — tasks are for scheduled/repetitive work, not chat results
import {
  SpaceBackground,
  Header,
  ChatBar,
  BottomNav,
  SideNav,
  Sidebar,
  SettingsPanel,

  AtomIcon,
  Ghost,
  Calendar,
  Mail,
  Smartphone,
  Globe,
  Layout,
  Loader
} from '../components/shared';
// GuidedTour removed
import { PulseBannerCompact } from '../components/PulseBanner';
// QuickActions + InputSuggestions removed — redundant with IntentBubbles
import { MomentCards } from '../components/MomentCard';
import { NotificationCenter } from '../components/ProactiveNotification';
import { DynamicView, parseDynamicView } from '../components/DynamicView';
import { DynamicViewV2 } from '../components/dynamic-view';
import type { ViewRecipe, BookingCardData } from '../components/dynamic-view';
import { hasTerminalExecutionEvidence } from '../services/workspaceEnvelope';
import { SuperJarvisPayCard, CartMandate } from '../components/pay/SuperJarvisPayCard';
// TrustDripCard removed — Trust DRIP is now conversational via Synapse DV, not modal overlays

// Generative HTML view recipe — AI generates a complete HTML micro-app
interface GenerativeHtmlRecipe {
  type: 'generative_html';
  html: string;
  evidence?: ViewRecipe['evidence'];
}

// Union type: standard block-based ViewRecipe OR generative HTML
type AnyViewRecipe = ViewRecipe | GenerativeHtmlRecipe;

// Message type for conversation
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
  isAcknowledgment?: boolean;  // For "On it..." placeholder
  agent?: string;
  tier?: string;
  sources?: string[];
  viewRecipe?: AnyViewRecipe;
}

// Sources Panel Component - slides out from right side
const SourcesPanel = ({
  isOpen,
  onClose,
  sources
}: {
  isOpen: boolean;
  onClose: () => void;
  sources: string[];
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={`fixed top-24 right-20 bottom-24 w-80 bg-black/95 backdrop-blur-xl border border-cyan-400/30 rounded-2xl shadow-[0_0_40px_rgba(34,211,238,0.2)] z-50 transform transition-all duration-300 ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-cyan-400/20">
        <h3 className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">Sources</h3>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-cyan-400/10 text-cyan-400/60 hover:text-cyan-300 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Sources list */}
      <div className="p-4 space-y-3 overflow-y-auto max-h-[calc(100%-60px)]">
        {sources.length === 0 ? (
          <p className="text-cyan-400/50 text-sm">No sources available for this response.</p>
        ) : (
          sources.map((source, index) => {
            // Normalize source to a proper URL — sources may come as domain names or partial URLs
            let displayName = source;
            let fullUrl = source;
            let isUrl = false;
            try {
              if (source.startsWith('http')) {
                const url = new URL(source);
                displayName = url.hostname.replace('www.', '');
                isUrl = true;
              } else if (source.includes('.') && !source.includes(' ')) {
                // Looks like a domain or partial URL (e.g., "google.com/flights")
                fullUrl = `https://${source}`;
                const url = new URL(fullUrl);
                displayName = url.hostname.replace('www.', '');
                isUrl = true;
              }
            } catch {
              // Not a valid URL, use as-is
              isUrl = false;
            }

            return (
              <div
                key={index}
                className="p-3 bg-cyan-400/5 border border-cyan-400/20 rounded-xl hover:bg-cyan-400/10 hover:border-cyan-400/40 transition-all cursor-pointer group"
                onClick={() => isUrl && window.open(fullUrl, '_blank', 'noopener,noreferrer')}
              >
                <div className="flex items-start gap-3">
                  <div className="p-1.5 rounded-lg bg-cyan-400/10 shrink-0">
                    <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-cyan-300 text-sm font-medium truncate group-hover:text-cyan-200">
                      {displayName}
                    </p>
                    {isUrl && (
                      <p className="text-cyan-400/40 text-xs truncate mt-0.5">
                        {fullUrl}
                      </p>
                    )}
                  </div>
                  {isUrl && (
                    <svg className="w-4 h-4 text-cyan-400/40 group-hover:text-cyan-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Simple markdown processor - strips markdown for clean display
function processMarkdown(text: string): React.ReactNode {
  // Process line by line, with table accumulation
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  // Helper: detect and render markdown tables
  const isTableLine = (l: string) => l.trim().startsWith('|') && l.trim().endsWith('|');
  const isSeparatorLine = (l: string) => /^\|[\s\-:|]+\|$/.test(l.trim());
  const parseTableCells = (l: string) =>
    l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

  // First pass: identify table blocks and render them
  let i = 0;
  const processedLines: { type: 'line' | 'table'; content: string; tableRows?: string[][] }[] = [];
  while (i < lines.length) {
    if (isTableLine(lines[i])) {
      // Accumulate table rows
      const tableLines: string[] = [];
      while (i < lines.length && (isTableLine(lines[i]) || isSeparatorLine(lines[i]))) {
        tableLines.push(lines[i]);
        i++;
      }
      // Parse table: first row = header, skip separator, rest = body
      const rows = tableLines.filter(l => !isSeparatorLine(l)).map(parseTableCells);
      if (rows.length > 0) {
        processedLines.push({ type: 'table', content: '', tableRows: rows });
      }
    } else {
      processedLines.push({ type: 'line', content: lines[i] });
      i++;
    }
  }

  // Render tables inline
  let lineIndex = 0;
  for (const item of processedLines) {
    if (item.type === 'table' && item.tableRows && item.tableRows.length > 0) {
      const [header, ...body] = item.tableRows;
      elements.push(
        <div key={`tbl-${lineIndex}`} className="my-3 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-cyan-400/30">
                {header.map((cell, ci) => (
                  <th key={ci} className="text-left py-2 px-3 text-cyan-300 font-semibold text-xs uppercase tracking-wider">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  {row.map((cell, ci) => (
                    <td key={ci} className="py-2 px-3 text-slate-300">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      lineIndex++;
      continue;
    }

    // Regular line processing below
    const line = item.content;

    let processed = line;
    const trimmed = processed.trim();

    // Remove #### / ### / ## / # headers but keep the text with styling
    // Strip **bold** markers from header/title text (already styled bold)
    const stripBold = (s: string) => s.replace(/\*\*(.+?)\*\*/g, '$1');

    if (trimmed.startsWith('#### ')) {
      processed = stripBold(trimmed.substring(5));
      elements.push(
        <div key={lineIndex} className="font-semibold text-cyan-300 mt-4 mb-2">
          {processed}
        </div>
      );
      lineIndex++; continue;
    } else if (trimmed.startsWith('### ')) {
      processed = stripBold(trimmed.substring(4));
      elements.push(
        <div key={lineIndex} className="font-semibold text-cyan-300 mt-4 mb-2">
          {processed}
        </div>
      );
      lineIndex++; continue;
    } else if (trimmed.startsWith('## ')) {
      processed = stripBold(trimmed.substring(3));
      elements.push(
        <div key={lineIndex} className="font-bold text-cyan-200 mt-5 mb-2 text-lg">
          {processed}
        </div>
      );
      lineIndex++; continue;
    } else if (trimmed.startsWith('# ')) {
      processed = stripBold(trimmed.substring(2));
      elements.push(
        <div key={lineIndex} className="font-bold text-cyan-100 mt-5 mb-3 text-xl">
          {processed}
        </div>
      );
      lineIndex++; continue;
    }

    // Standalone **bold** lines (AI uses these as section headers without #)
    if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
      processed = trimmed.replace(/^\*\*(.+)\*\*$/, '$1');
      elements.push(
        <div key={lineIndex} className="font-semibold text-cyan-300 mt-4 mb-2">
          {processed}
        </div>
      );
      lineIndex++; continue;
    }

    // Handle bullet points (* item or - item)
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
      const bulletContent = trimmed.substring(2);
      // Render bold inline
      const bulletParts: React.ReactNode[] = [];
      let bRemaining = bulletContent;
      let bIdx = 0;
      while (bRemaining.length > 0) {
        const bm = bRemaining.match(/\*\*(.+?)\*\*/);
        if (!bm) { bulletParts.push(bRemaining); break; }
        const pos = bRemaining.indexOf(bm[0]);
        if (pos > 0) bulletParts.push(bRemaining.substring(0, pos));
        bulletParts.push(<strong key={`bb-${lineIndex}-${bIdx++}`} className="font-semibold text-cyan-200">{bm[1]}</strong>);
        bRemaining = bRemaining.substring(pos + bm[0].length);
      }
      elements.push(
        <div key={lineIndex} className="flex items-start gap-2 my-1.5 ml-3">
          <span className="text-cyan-400 mt-0.5 text-xs">•</span>
          <span>{bulletParts}</span>
        </div>
      );
      lineIndex++; continue;
    }

    // Handle --- horizontal rule
    if (trimmed === '---' || trimmed === '***') {
      elements.push(
        <hr key={lineIndex} className="border-cyan-400/20 my-4" />
      );
      lineIndex++; continue;
    }

    // Handle empty lines
    if (trimmed === '') {
      elements.push(<div key={lineIndex} className="h-2" />);
      lineIndex++; continue;
    }

    // Note: bold/code markers are handled inline below via React elements

    // Parse links: markdown [text](url) and raw URLs
    const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s,)<]+)/g;
    const parts: React.ReactNode[] = [];
    let lastIdx = 0;
    let linkMatch;

    while ((linkMatch = linkPattern.exec(processed)) !== null) {
      // Text before the link
      if (linkMatch.index > lastIdx) {
        parts.push(processed.substring(lastIdx, linkMatch.index));
      }

      const linkUrl = linkMatch[2] || linkMatch[3];  // markdown url or raw URL
      const displayText = linkMatch[1] || (() => {
        try { return new URL(linkUrl).hostname.replace('www.', ''); } catch { return linkUrl; }
      })();

      parts.push(
        <a
          key={`link-${lineIndex}-${linkMatch.index}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 decoration-cyan-400/40 hover:decoration-cyan-300 transition-colors"
        >
          {displayText}
        </a>
      );

      lastIdx = linkMatch.index + linkMatch[0].length;
    }

    // Remaining text after last link
    if (lastIdx < processed.length) {
      parts.push(processed.substring(lastIdx));
    }

    // Now process bold (**text**) and code (`text`) in each text part
    const processInline = (node: React.ReactNode, idx: number): React.ReactNode => {
      if (typeof node !== 'string') return node;
      // Split on **bold** and `code` patterns
      const segments: React.ReactNode[] = [];
      let remaining = node;
      let segIdx = 0;
      while (remaining.length > 0) {
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        const codeMatch = remaining.match(/`([^`]+)`/);
        // Find whichever comes first
        const boldPos = boldMatch ? remaining.indexOf(boldMatch[0]) : Infinity;
        const codePos = codeMatch ? remaining.indexOf(codeMatch[0]) : Infinity;
        if (boldPos === Infinity && codePos === Infinity) {
          segments.push(remaining);
          break;
        }
        if (boldPos <= codePos && boldMatch) {
          if (boldPos > 0) segments.push(remaining.substring(0, boldPos));
          segments.push(<strong key={`b-${idx}-${segIdx++}`} className="font-semibold text-cyan-200">{boldMatch[1]}</strong>);
          remaining = remaining.substring(boldPos + boldMatch[0].length);
        } else if (codeMatch) {
          if (codePos > 0) segments.push(remaining.substring(0, codePos));
          segments.push(<code key={`c-${idx}-${segIdx++}`} className="bg-cyan-400/10 px-1.5 py-0.5 rounded text-cyan-300 text-xs font-mono">{codeMatch[1]}</code>);
          remaining = remaining.substring(codePos + codeMatch[0].length);
        }
      }
      return segments.length > 1 ? segments : segments[0] || node;
    };

    const finalParts = (parts.length > 0 ? parts : [processed]).map((p, i) => processInline(p, i));

    elements.push(
      <React.Fragment key={lineIndex}>
        {finalParts}
        {lineIndex < processedLines.length - 1 && <br />}
      </React.Fragment>
    );
    lineIndex++;
  }

  return elements;
}

/**
 * Inject a postMessage bridge script into generative HTML before rendering in an iframe.
 * Intercepts button clicks with data-action attributes and forwards them to the parent window.
 */
function injectDVBridgeScript(html: string): string {
  const bridgeScript = `<script>
(function() {
  // Global action helper
  window.superjarvis = {
    action: function(type, data) {
      parent.postMessage({ source: 'superjarvis-dv', type: type, data: data || {} }, '*');
    }
  };
  // Auto-resize: report height to parent
  function reportHeight() {
    var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    parent.postMessage({ source: 'superjarvis-dv', type: 'resize', data: { height: h } }, '*');
  }
  if (window.ResizeObserver) {
    new ResizeObserver(reportHeight).observe(document.body);
  }
  setTimeout(reportHeight, 200);
  // Intercept clicks on buttons/links
  document.addEventListener('click', function(e) {
    var el = e.target;
    while (el && el !== document.body) {
      if (el.dataset && el.dataset.action) {
        e.preventDefault();
        var action = el.dataset.action;
        var data = {};
        if (el.dataset) {
          for (var key in el.dataset) {
            if (key !== 'action') data[key] = el.dataset[key];
          }
        }
        var type = action;
        if (action === 'select' || action === 'select_flight' || action === 'select-flight') type = 'select_flight';
        if (action === 'book' || action === 'booking' || action === 'checkout' || action === 'confirm' || action === 'pay' || action === 'proceed') type = 'book';
        if (action === 'refine') { parent.postMessage({ source: 'superjarvis-dv', type: 'refine', data: { query: data.query || el.textContent } }, '*'); return; }
        parent.postMessage({ source: 'superjarvis-dv', type: type, data: data }, '*');
        return;
      }
      // Fallback: catch buttons without data-action by text content
      if (el.tagName === 'BUTTON' || el.tagName === 'A') {
        var txt = (el.textContent || '').toLowerCase().trim();
        // Calendar invite/event confirm — event was already created, just acknowledge
        if (txt.includes('confirm') && (txt.includes('invite') || txt.includes('calendar') || txt.includes('event') || txt.includes('meeting') || txt.includes('schedule') || txt.includes('send'))) {
          e.preventDefault();
          el.textContent = '\u2713 Sent!';
          el.disabled = true;
          el.style.opacity = '0.6';
          el.style.cursor = 'default';
          parent.postMessage({ source: 'superjarvis-dv', type: 'calendar_confirmed', data: {} }, '*');
          return;
        }
        if (txt.includes('proceed') || txt.includes('checkout') || txt.includes('confirm') || txt.includes('pay')) {
          e.preventDefault();
          var btnData = {};
          if (el.dataset) { for (var k in el.dataset) { btnData[k] = el.dataset[k]; } }
          parent.postMessage({ source: 'superjarvis-dv', type: 'book', data: btnData }, '*');
          return;
        }
        if (txt.includes('select flight') || txt.includes('select')) {
          e.preventDefault();
          var selData = {};
          if (el.dataset) { for (var sk in el.dataset) { selData[sk] = el.dataset[sk]; } }
          parent.postMessage({ source: 'superjarvis-dv', type: 'select_flight', data: selData }, '*');
          return;
        }
      }
      el = el.parentElement;
    }
  });
})();
</script>`;
  // Inject before </body> or at end
  if (html.includes('</body>')) {
    return html.replace('</body>', bridgeScript + '</body>');
  }
  return html + bridgeScript;
}

// Intent Bubble Component - 25% larger
const IntentBubble = ({ text, icon: Icon, onClick }: { text: string; icon: React.ElementType; onClick?: () => void }) => (
  <button
    onClick={onClick}
    className="group flex items-center space-x-4 bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] active:bg-cyan-400/20 backdrop-blur-xl border border-cyan-400/30 hover:border-cyan-400/60 px-5 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 text-left w-full shadow-[0_0_10px_rgba(34,211,238,0.05)] hover:shadow-[0_0_25px_rgba(34,211,238,0.3),0_0_50px_rgba(34,211,238,0.15)] max-w-lg"
  >
    {Icon && (
      <div className="p-1.5 rounded bg-cyan-400/10 border border-cyan-400/30 group-hover:border-cyan-400/60 transition-colors shrink-0">
        <Icon className="w-4 h-4 text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.8)]" />
      </div>
    )}
    <span className="text-cyan-400 text-sm font-semibold tracking-wide drop-shadow-[0_0_5px_rgba(34,211,238,0.3)] group-hover:text-cyan-300 whitespace-nowrap">{text}</span>
  </button>
);

export default function HomeScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { user, signOut } = useAuthContext();
  const { openGhost, startShadowRecord } = useGhost();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState(() => {
    // Persist mode selection across page refreshes
    return localStorage.getItem('superjarvis_chat_mode') || 'Super';
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [progressMessage, setProgressMessage] = useState('');  // User-friendly progress updates
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sourcesPanel, setSourcesPanel] = useState<{ isOpen: boolean; sources: string[] }>({ isOpen: false, sources: [] });
  const [payCardMandate, setPayCardMandate] = useState<CartMandate | null>(null);
  const pendingMessageRef = useRef<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<Message[]>([]);
  const activeViewRecipeRef = useRef<AnyViewRecipe | undefined>(undefined);
  const conversationIdRef = useRef<string | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
    const latestAssistant = [...messages].reverse().find(m => m.role === 'assistant' && !m.isAcknowledgment);
    activeViewRecipeRef.current = latestAssistant?.viewRecipe as AnyViewRecipe | undefined;
  }, [messages]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Get conversationId from URL params (for loading existing conversations from Recents)
  const urlConversationId = searchParams.get('conversationId');
  const urlMessage = searchParams.get('message');

  // Get user's first name for greeting
  const userName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || null;

  // Onboarding removed — users go straight to HomeScreen
  // Mark as complete for any legacy checks
  useEffect(() => {
    if (user) {
      localStorage.setItem('superjarvis_onboarding_complete', 'true');
    }
  }, [user]);

  // Load existing conversation if conversationId is in URL
  useEffect(() => {
    if (urlConversationId && user && !conversationId) {
      loadConversation(urlConversationId);
    }
  }, [urlConversationId, user]);

  // Clear conversation when navigating to home without a conversationId or Message (New Chat)
  // Clear conversation ONLY on explicit New Chat navigation (not on DV button clicks or state changes)
  // Previous code cleared on any location.key change which destroyed conversation continuity
  // when DV buttons fired handleAction → handleSendMessage
  const prevLocationKey = useRef(location.key);
  const suppressNextRootClear = useRef(false);
  useEffect(() => {
    // Only clear if location.key actually changed (not on initial mount)
    if (prevLocationKey.current === location.key) return;
    prevLocationKey.current = location.key;

    // Sidebar/Recent message injection cleans the ?message param after dispatch.
    // That URL cleanup causes a root navigation with no query params, which this
    // effect previously mistook for a genuine "New Chat" action.
    if (suppressNextRootClear.current) {
      suppressNextRootClear.current = false;
      return;
    }

    // Only clear if there's no conversation context in the URL AND we have messages
    // AND the navigation was to the root "/" (New Chat), not a DV button action
    // Also don't clear if we have an active conversation (prevents race after URL cleanup)
    if (!urlConversationId && !urlMessage && messages.length > 0 && !conversationId) {
      // Check if this is a genuine "New Chat" navigation by looking at the URL
      const isNewChat = window.location.pathname === '/' && !window.location.search;
      if (isNewChat) {
        setMessages([]);
        setConversationId(null);
        setInputText('');
        setStreamingContent('');
        setProgressMessage('');
        // Also clear loading state so user can start fresh even if previous
        // request was still pending (SSE stream timeout, disconnection, etc.)
        setIsLoading(false);
      }
    }
  }, [urlConversationId, urlMessage, location.key, isLoading, messages.length]);

  const loadConversation = async (convId: string) => {
    if (!user) return;

    setIsLoadingConversation(true);
    try {
      // Get conversation metadata
      const conversation = await getConversation(user.uid, convId);
      if (!conversation) {
        console.error('[HomeScreen] Conversation not found:', convId);
        setIsLoadingConversation(false);
        return;
      }

      // Get messages
      const firestoreMessages = await getMessages(user.uid, convId);

      if (firestoreMessages.length > 0) {
        // Convert Firestore messages to local Message format (including persisted viewRecipe + sources)
        const loadedMessages: Message[] = firestoreMessages.map((msg, index) => ({
          id: msg.id || `loaded-${index}`,
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
          timestamp: msg.timestamp?.toDate() || new Date(),
          agent: msg.agent,
          tier: msg.tier,
          sources: msg.sources,
          viewRecipe: msg.viewRecipe as Message['viewRecipe'],
        }));

        setMessages(loadedMessages);
        setConversationId(convId);
      }
    } catch (error) {
      console.error('[HomeScreen] Error loading conversation:', error);
    } finally {
      setIsLoadingConversation(false);
    }
  };

  // Persist chat mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('superjarvis_chat_mode', chatMode);
  }, [chatMode]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // postMessage bridge — listen for actions from generative HTML iframes
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== 'object') return;
      const msg = e.data;
      // Support both direct events and superjarvis-dv bridge events
      const type = msg.type;
      const data = msg.data || msg;
      const prompt = msg.prompt || data?.prompt;
      const url = msg.url || data?.url;
      const provider = msg.provider || data?.provider;
      const guardTerminalAction = (actionLabel: string): boolean => {
        if (hasTerminalExecutionEvidence(activeViewRecipeRef.current as ViewRecipe | null | undefined)) {
          return true;
        }

        const truthMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `I can't ${actionLabel} from this Dynamic View yet because the backend has not logged provider-backed execution evidence for that terminal action.`,
          agent: 'SuperJarvis Stage',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, truthMessage]);
        return false;
      };
      const promptLooksTerminal = typeof prompt === 'string' && /\b(book|reserve|pay|checkout|purchase|send|finalize|confirm|select)\b|check availability/i.test(prompt);
      switch (type) {
        case 'action':
          if (promptLooksTerminal && !guardTerminalAction('run that terminal action')) break;
          if (prompt) handleAction(prompt);
          break;
        case 'refine':
          if (prompt || data?.query) handleAction(prompt || data.query);
          break;
        case 'calendar_confirmed':
          // Calendar event was already created on first pass — just acknowledge, no re-trigger
          break;
        case 'select_flight': {
          if (!guardTerminalAction('select that booking option')) break;
          // User clicked "Select Flight" in the DV canvas
          // Include ALL flight details so the AI generates an accurate booking summary
          const airline = data?.airline || data?.flightId || 'selected flight';
          const price = data?.price || '';
          const origin = data?.origin || data?.from || '';
          const destination = data?.destination || data?.to || '';
          const departure = data?.departure || data?.departureTime || data?.depart || '';
          const arrival = data?.arrival || data?.arrivalTime || data?.arrive || '';
          const flightNumber = data?.flightNumber || data?.flight || '';
          const cabin = data?.cabin || data?.class || '';
          const stops = data?.stops ?? '';
          const duration = data?.duration || '';
          const passengers = data?.passengers || '';
          // Build a detailed prompt with all available flight context
          const details = [
            airline,
            flightNumber && `flight ${flightNumber}`,
            origin && destination && `from ${origin} to ${destination}`,
            departure && `departing ${departure}`,
            arrival && `arriving ${arrival}`,
            duration && `(${duration})`,
            stops !== '' && (stops === 0 || stops === '0' ? 'non-stop' : `${stops} stop(s)`),
            cabin && `${cabin} class`,
            price && `at ${price}`,
            passengers && `for ${passengers} passenger(s)`,
          ].filter(Boolean).join(', ');
          handleAction(`I want to book this specific flight: ${details}. Show me the booking summary with the correct flight details and checkout.`);
          break;
        }
        case 'checkout':
        case 'confirm':
        case 'book': {
          if (!guardTerminalAction('open checkout for that booking')) break;
          // User clicked a booking/pay/checkout action in the DV
          const merchantName = data?.merchantName || data?.airline || '';
          const amount = parseFloat(data?.amount || data?.price || '0');
          // Create payment mandate directly — Trust DRIP is handled conversationally by Synapse
          if (merchantName || amount > 0) {
            const mandate: CartMandate = {
              id: crypto.randomUUID(),
              merchantId: data?.merchantId || 'dynamic-view',
              merchantName: merchantName || 'Flight Booking',
              originalAmount: amount,
              finalAmount: amount,
              currency: data?.currency || 'CAD',
              items: [{ name: data?.description || merchantName || 'Booking', price: amount }],
              agentReasoning: `Booking requested via Dynamic View for ${merchantName || 'selected item'}`,
            };
            setPayCardMandate(mandate);
          } else {
            handleAction('Proceed to checkout. Show me the SuperJarvis Pay confirmation for this booking.');
          }
          break;
        }
        case 'pay':
          if (!guardTerminalAction('pay from that Dynamic View')) break;
          if (data?.merchantName) {
            const mandate: CartMandate = {
              id: crypto.randomUUID(),
              merchantId: data.merchantId || 'dynamic-view',
              merchantName: data.merchantName,
              originalAmount: data.amount || 0,
              finalAmount: data.amount || 0,
              currency: data.currency || 'USD',
              items: data.items || [{ name: data.description || data.merchantName, price: data.amount || 0 }],
              agentReasoning: `Payment requested via Dynamic View for ${data.merchantName}`,
            };
            setPayCardMandate(mandate);
          }
          break;
        case 'connect': {
          const prov = provider || data?.provider || '';
          if (prov === 'other') {
            // User wants a provider we don't have — ask in chat, Conduit Forge will handle
            handleAction('I want to connect a different calendar/travel app. What options do you support?');
          } else if (prov) {
            // Map common provider IDs to integration categories
            const categoryMap: Record<string, string> = {
              google_calendar: 'calendar', outlook_calendar: 'calendar', apple_calendar: 'calendar',
              gmail: 'email', outlook_email: 'email', icloud_email: 'email',
              slack: 'messages', teams: 'messages', discord: 'messages',
            };
            const category = categoryMap[prov];
            if (category) {
              navigate(`/integrations/${category}`);
            } else {
              // Unknown provider — let the AI handle via Conduit Forge
              handleAction(`Connect my ${prov} account`);
            }
          }
          break;
        }
        case 'navigate':
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
          break;
        case 'resize':
          // Iframe auto-resize — handled by CSS in this layout
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Pay card handlers
  const handlePaymentApprove = async (mandateId: string, signature: string) => {
    console.log(`[Vault] Mandate ${mandateId} signed:`, signature);
    try {
      const token = user ? await (user as any).getIdToken?.() : null;
      const gatewayUrl = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const approveResponse = await fetch(`${gatewayUrl}/pay/mandate/${mandateId}/approve`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({ signature }),
      });
      if (!approveResponse.ok) {
        throw new Error(`Payment approval failed (${approveResponse.status})`);
      }

      const approvePayload = await approveResponse.json().catch(() => ({}));
      const approvalToken = approvePayload?.approval_token;
      const executeHeaders = approvalToken
        ? { ...authHeaders, 'X-Payment-Approval-Token': approvalToken }
        : authHeaders;

      const response = await fetch(`${gatewayUrl}/pay/mandate/${mandateId}/execute`, {
        method: 'POST',
        headers: executeHeaders,
        credentials: 'include',
        body: JSON.stringify({ signature }),
      });
      if (!response.ok) throw new Error(`Payment failed (${response.status})`);
      const executePayload = await response.json().catch(() => ({}));
      const paymentUrl = executePayload?.payment_url;
      const stripeId = executePayload?.stripe_id;
      const receiptMsg: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: paymentUrl || stripeId
          ? `Payment execution confirmed for mandate \`${mandateId.substring(0, 8)}\`${stripeId ? ` (Stripe: ${stripeId})` : ''}${paymentUrl ? `. Receipt: ${paymentUrl}` : ''}`
          : `Payment execution confirmed for mandate \`${mandateId.substring(0, 8)}\`.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, receiptMsg]);
      setPayCardMandate(null);
    } catch (err: any) {
      const errMsg: Message = { id: Date.now().toString(), role: 'assistant', content: `Payment failed (\`${mandateId.substring(0, 8)}\`): ${err.message}`, timestamp: new Date() };
      setMessages(prev => [...prev, errMsg]);
      throw err;
    }
  };

  const handlePaymentDecline = (mandateId: string) => {
    console.log(`[Vault] Mandate ${mandateId} declined`);
    setPayCardMandate(null);
  };

  const handlePaymentUndo = (mandateId: string) => {
    console.log(`[Vault] Undo mandate ${mandateId}`);
    const undoMsg: Message = { id: Date.now().toString(), role: 'assistant', content: `Transaction reversed for mandate \`${mandateId.substring(0, 8)}\`.`, timestamp: new Date() };
    setMessages(prev => [...prev, undoMsg]);
  };

  const handleBookingAction = (bookingData: BookingCardData) => {
    const merchantName = bookingData.provider || bookingData.title || 'Booking';
    const parsedAmount = typeof bookingData.price === 'string'
      ? parseFloat(bookingData.price.replace(/[^0-9.\-]/g, ''))
      : Number(bookingData.price || 0);
    const amount = Number.isFinite(parsedAmount) ? parsedAmount : 0;

    if (merchantName || amount > 0) {
      const mandate: CartMandate = {
        id: crypto.randomUUID(),
        merchantId: 'dynamic-view-v2',
        merchantName,
        originalAmount: amount,
        finalAmount: amount,
        currency: bookingData.currency || 'CAD',
        items: [{
          name: bookingData.title || merchantName,
          price: amount,
        }],
        agentReasoning: `Booking requested via Dynamic View V2 for ${bookingData.title || merchantName}`,
      };
      setPayCardMandate(mandate);
      return;
    }

    if (bookingData.bookingUrl) {
      window.open(bookingData.bookingUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    handleAction(`Proceed with booking ${bookingData.title || merchantName}. Show me the SuperJarvis Pay confirmation for this booking.`);
  };

  // Auto-close auth modal when user signs in
  useEffect(() => {
    if (user && authModalOpen) {
      setAuthModalOpen(false);
      // If we have a pending action, execute it
      if (pendingMessageRef.current) {
        const pending = pendingMessageRef.current;
        pendingMessageRef.current = '';
        // Check if it's a navigation action
        if (pending.startsWith('__navigate:')) {
          const path = pending.replace('__navigate:', '');
          setTimeout(() => navigate(path), 100);
        } else {
          // It's a chat message
          setTimeout(() => handleSendMessage(pending), 100);
        }
      }
    }
  }, [user, authModalOpen, navigate]);

  // Map UI mode labels to API execution mode values
  const getExecutionMode = (mode: string): ExecutionMode => {
    switch (mode) {
      case 'Fast': return 'fast';
      case 'Super': return 'super';
      default: return 'super';  // SUPER is the default mode
    }
  };

  // Handle sending a message and getting response
  const handleSendMessage = async (message: string) => {
    if (!message.trim() || !user || isLoading) return;

    // Add user message to conversation
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message.trim(),
      timestamp: new Date()
    };

    // Add immediate acknowledgment message - never leave user waiting
    const ackMessage: Message = {
      id: 'ack-' + Date.now().toString(),
      role: 'assistant',
      content: 'On it...',
      timestamp: new Date(),
      isAcknowledgment: true  // Flag to replace later
    };
    let targetAssistantMessageId = ackMessage.id;

    setMessages(prev => [...prev, userMessage, ackMessage]);
    setInputText('');
    setIsLoading(true);

    // Create or use existing conversation for Firestore persistence
    let convId = conversationIdRef.current;
    if (!convId) {
      try {
        convId = await createConversation(user.uid, message.trim());
        setConversationId(convId);
        console.log('[HomeScreen] Created conversation:', convId);
      } catch (error) {
        console.error('[HomeScreen] Error creating conversation:', error);
      }
    }

    // Save user message to Firestore
    if (convId) {
      try {
        await addMessageToFirestore(user.uid, convId, {
          role: 'user',
          content: message.trim()
        });
      } catch (error) {
        console.error('[HomeScreen] Error saving user message:', error);
      }
    }

    // Build history from current conversation using the latest state snapshot,
    // excluding acknowledgment placeholders and including the message we just added.
    const conversationHistory = [
      ...messagesRef.current
        .filter(m => !m.isAcknowledgment)
        .map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        })),
      {
        role: userMessage.role as 'user' | 'assistant',
        content: userMessage.content,
      },
    ];

    // Track streaming content for this request
    let fullResponse = '';
    const finalConvId = convId;

    // Use streaming API for real-time SSE updates
    await sendMessageStream(
      message.trim(),
      { executionMode: getExecutionMode(chatMode) },
      conversationHistory,
      // onChunk - called for each SSE event
      (chunk: StreamChunk) => {
        if (chunk.type === 'content') {
          fullResponse += chunk.data;
          setStreamingContent(fullResponse);
          setProgressMessage('');  // Clear progress when content arrives
        } else if (chunk.type === 'progress') {
          // User-friendly progress messages (no technical lane names)
          setProgressMessage(chunk.data);
        } else if (chunk.type === 'lane_start' || chunk.type === 'lane_progress' || chunk.type === 'lane_complete') {
          // Lane progress events - logged but not shown to users (IP protection)
          console.log(`[HomeScreen] Lane event: ${chunk.type}`, chunk.data);
        } else if (chunk.type === 'dynamic_view_ready') {
          const dvData = (chunk as any).view_recipe || (typeof chunk.data === 'string' ? (() => { try { return JSON.parse(chunk.data); } catch { return null; } })() : chunk.data);
          if (dvData) {
            console.log('[HomeScreen] Dynamic View ready (async):', dvData.type || 'unknown');
            setMessages(prev => prev.map(m => {
              if (m.id === targetAssistantMessageId) {
                return { ...m, viewRecipe: dvData as AnyViewRecipe };
              }
              // Fallback: if onComplete hasn't fired yet, the ack message
              // still has the original id.  Attach the DV to it so the user
              // sees the result even if the 'complete' SSE event was lost.
              if (m.id === ackMessage.id && m.isAcknowledgment) {
                return { ...m, viewRecipe: dvData as AnyViewRecipe, content: fullResponse || m.content };
              }
              return m;
            }));
            // Safety: if DV arrived, the stream is effectively done.
            // Clear loading so the user can interact again.
            setIsLoading(false);
            setStreamingContent('');
            setProgressMessage('');
          }
        }
      },
      // onComplete - called when stream finishes
      (result: IntentResponse) => {
        // Clean up any leftover XML tags from the response
        let assistantContent = result.result as string || fullResponse || 'I apologize, but I could not generate a response.';
        assistantContent = assistantContent.replace(/<\/?(?:voice|dynamic_view|pay_request)>/g, '').trim();
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: assistantContent,
          timestamp: new Date(),
          agent: result.agent || 'GENESIS',
          tier: result.tier,
          sources: result.sources || [],
          viewRecipe: result.view_recipe,
        };
        targetAssistantMessageId = assistantMessage.id;

        // Replace the specific ack message with the final response so async DV
        // events stay bound to the correct turn.
        setMessages(prev => prev.map(m => m.id === ackMessage.id ? assistantMessage : m));
        setStreamingContent('');
        setProgressMessage('');
        setIsLoading(false);

        // Save assistant message to Firestore (including viewRecipe + sources for persistence)
        if (finalConvId) {
          const messageToSave: Parameters<typeof addMessageToFirestore>[2] = {
            role: 'assistant',
            content: assistantContent,
            agent: result.agent || 'GENESIS',
            ...(result.tier && { tier: result.tier }),
            ...(result.sources && result.sources.length > 0 && { sources: result.sources }),
            ...(result.view_recipe && { viewRecipe: result.view_recipe }),
          };
          addMessageToFirestore(user.uid, finalConvId, messageToSave).catch(error => {
            console.error('[HomeScreen] Error saving assistant message:', error);
          });
        }
      },
      // onError - called if stream fails
      (error: Error) => {
        const errorContent = `Error: ${error.message || 'Something went wrong. Please try again.'}`;
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: errorContent,
          timestamp: new Date()
        };
        setMessages(prev => prev.map(m => m.id === ackMessage.id ? errorMessage : m));
        setStreamingContent('');
        setProgressMessage('');
        setIsLoading(false);
      }
    );
  };

  // Handle intent/chat action - check auth first
  const handleAction = (message: string) => {
    const normalized = message.trim().toLowerCase();

    // S1 guardrail: "Book a Trip" should prefill a concrete editable prompt
    // instead of auto-sending a generic ambiguous action.
    if (normalized === 'book a trip') {
      setInputText('Book me a round-trip flight from Vancouver to San Francisco next Friday morning, returning Sunday evening.');
      return;
    }

    if (user) {
      // User is authenticated, send message directly
      handleSendMessage(message);
    } else {
      // Not authenticated, store message and show auth modal
      pendingMessageRef.current = message;
      setAuthModalOpen(true);
    }
  };

  // Process incoming 'message' from URL query (e.g. sidebar Calendar/Email buttons,
  // Ghost/Shadow Mode post-save conversion prompts).
  useEffect(() => {
    // Gate on user being loaded — Firebase auth resolves async, so urlMessage
    // can be set before user is available. Without this gate, handleSendMessage
    // silently returns because !user is true.
    //
    // IMPORTANT: do NOT block on !conversationId.
    // Shadow Mode conversion currently navigates with ?message=... while the user
    // may already be in an active conversation. If we require !conversationId,
    // the saved recording prompt is dropped and the user sees no post-recording
    // magic even though the recording persisted successfully.
    if (urlMessage && !isLoading && user) {
      handleAction(urlMessage);

      // Prevent the root-clear effect from wiping the conversation when we
      // remove ?message from the URL after dispatch.
      suppressNextRootClear.current = true;

      // Clean up URL to prevent triggering multiple times on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('message');
      newParams.delete('mode');
      setSearchParams(newParams, { replace: true });
    }
  }, [urlMessage, isLoading, user]);

  // Handle chat bar submission
  const handleChatSubmit = () => {
    if (!isLoading && inputText.trim()) {
      handleAction(inputText.trim());
    }
  };

  // When auth modal closes, check if user is now authenticated
  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
    // If user just authenticated and we have a pending action, execute it
    if (user && pendingMessageRef.current) {
      const pending = pendingMessageRef.current;
      pendingMessageRef.current = '';
      // Check if it's a navigation action
      if (pending.startsWith('__navigate:')) {
        const path = pending.replace('__navigate:', '');
        navigate(path);
      } else {
        // It's a chat message
        handleSendMessage(pending);
      }
    }
  };

  // Check if we're in conversation mode (has messages or loading a conversation)
  const inConversation = messages.length > 0 || isLoadingConversation;

  // Detect if the latest real assistant turn has a generative HTML canvas.
  // Do not keep an older canvas active once newer turns have moved on.
  const latestAssistant = [...messages].reverse().find(m => m.role === 'assistant' && !m.isAcknowledgment);
  const activeCanvas = latestAssistant?.viewRecipe as AnyViewRecipe | undefined;
  const isGenerativeHtml = activeCanvas && 'type' in activeCanvas && activeCanvas.type === 'generative_html';
  const hasCanvas = !!isGenerativeHtml;

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans selection:bg-cyan-400/20 selection:text-cyan-100">
      <SpaceBackground />

      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* Center/Body */}
        <div className={`flex-1 flex ${hasCanvas && inConversation ? 'flex-row overflow-hidden' : 'flex-col'} ${hasCanvas && inConversation ? '' : 'pb-36'} ${hasCanvas && inConversation ? 'px-0' : 'px-4'} ${hasCanvas && inConversation ? '' : inConversation ? '' : 'pb-48'} ${hasCanvas && inConversation ? '' : 'overflow-y-auto'} w-full ${hasCanvas && inConversation ? '' : 'items-center'}`}>

          {/* Loading conversation indicator */}
          {isLoadingConversation && (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-4">
                <Loader className="w-12 h-12 text-cyan-400 animate-spin" />
                <p className="text-cyan-400/60 text-sm">Loading conversation...</p>
              </div>
            </div>
          )}

          {!inConversation ? (
            /* Landing View - Greeting, Moment Cards, and Intent Bubbles */
            <div className="w-full max-w-xl flex flex-col justify-center flex-1 pr-0">
              <div className="flex flex-col items-start space-y-5">
                {/* Greeting + Logo */}
                <div className="flex items-center space-x-4 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-700">
                  <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
                  <span className="text-4xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] font-sans">
                    {userName ? `Hi ${userName},` : 'Hello,'}
                  </span>
                </div>

                {/* Sign In prompt for guests */}
                {!user && (
                  <button
                    onClick={() => setAuthModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-400/10 border border-cyan-400/30 hover:bg-cyan-400/20 hover:border-cyan-400/50 transition-all duration-300 text-cyan-300 text-sm font-medium shadow-[0_0_15px_rgba(34,211,238,0.1)] hover:shadow-[0_0_25px_rgba(34,211,238,0.3)]"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Sign in to get started
                  </button>
                )}

                {/* Moment Cards - contextual information */}
                <MomentCards
                  onAction={(prompt) => handleAction(prompt)}
                  layout="horizontal"
                  maxCards={2}
                />

                {/* Vertical Intent Suggestions */}
                <div className="flex flex-col items-start space-y-4 w-full">
                  <IntentBubble icon={Ghost} text="Learn to do my work with Shadow Mode" onClick={() => startShadowRecord()} />
                  <IntentBubble icon={Calendar} text="Make a reservation" onClick={() => handleAction("Make a reservation")} />
                  <IntentBubble icon={Mail} text="Organize my email" onClick={() => handleAction("Organize my email")} />
                  <IntentBubble icon={Smartphone} text="Build an app" onClick={() => { if (user) { navigate('/apps/create'); } else { pendingMessageRef.current = '__navigate:/apps/create'; setAuthModalOpen(true); } }} />
                  <IntentBubble icon={Globe} text="Book a Trip" onClick={() => setInputText("Book me a round-trip flight from Vancouver to San Francisco next Friday morning, returning Sunday evening.")} />
                  <IntentBubble icon={Layout} text="Plan, schedule, and book my next week" onClick={() => handleAction("Plan, schedule, and book my next week")} />
                </div>
              </div>
            </div>
          ) : hasCanvas ? (
            /* SPLIT LAYOUT — Chat sidebar left, Dynamic View canvas right */
            <>
              {/* Left: Chat messages — scrollable with room for fixed chat bar */}
              <div className="w-[420px] md:w-[480px] lg:w-[520px] flex-shrink-0 flex flex-col space-y-4 px-4 overflow-y-auto border-r border-cyan-400/10 pb-40">
                {/* Chat title */}
                <div className="sticky top-0 z-20 py-3 bg-gradient-to-b from-black via-black/80 to-transparent">
                  <h2 className="text-sm font-semibold text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] truncate">
                    {messages.find(m => m.role === 'user')?.content.substring(0, 40) || 'New Chat'}
                    {(messages.find(m => m.role === 'user')?.content.length || 0) > 40 ? '...' : ''}
                  </h2>
                </div>

                {messages.map((message) => (
                  <div
                    key={message.id}
                    data-testid={`chat-message-${message.role}`}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`w-full px-4 py-3 rounded-xl ${message.role === 'user'
                      ? 'bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 text-sm'
                      : 'text-cyan-300'}`}
                    >
                      {/* In split layout, always render text — DV2 blocks are in the canvas */}
                      <div className={`leading-relaxed ${message.role === 'user' ? 'text-sm' : 'text-sm md:text-base'} prose prose-invert prose-cyan max-w-none [&>p]:mb-3 [&>ul]:mb-3 [&>ul]:ml-4 [&>ul>li]:mb-1 [&>ol]:mb-3 [&>ol]:ml-4 [&>h2]:text-cyan-300 [&>h2]:font-semibold [&>h2]:mt-4 [&>h2]:mb-2 [&>h3]:text-cyan-300 [&>h3]:font-medium [&>h3]:mt-3 [&>h3]:mb-1 [&>strong]:text-cyan-200`}>{processMarkdown(message.content)}</div>
                      {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                        <button
                          onClick={() => setSourcesPanel({ isOpen: true, sources: message.sources || [] })}
                          className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] text-cyan-400/70 hover:text-cyan-300 border border-cyan-400/20 rounded-full transition-all"
                        >
                          {message.sources.length} Source{message.sources.length !== 1 ? 's' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div data-testid="home-chat-loading" className="px-3 py-2.5 rounded-xl bg-cyan-500/[0.05] border border-cyan-400/20">
                    {streamingContent ? (
                      <div className="text-sm leading-relaxed text-cyan-300">
                        {streamingContent}
                        <span className="inline-block w-1.5 h-3.5 bg-cyan-400 ml-1 animate-pulse" />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <AtomIcon className="w-4 h-4 text-cyan-400 animate-spin-slow" />
                        <span className="text-cyan-400/60 text-xs">{progressMessage || 'Thinking...'}</span>
                      </div>
                    )}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Right: Dynamic View Canvas — scrollable, room for side nav + chat bar */}
              <div className="flex-1 relative overflow-y-auto mr-20 pb-40 h-full">
                {isGenerativeHtml && (
                  <iframe
                    srcDoc={injectDVBridgeScript((activeCanvas as GenerativeHtmlRecipe).html)}
                    /* No sandbox — DV content is generated by our own AI agents, postMessage bridge requires same-origin */
                    className="w-full border-0"
                    style={{ background: '#0a0a0f', borderRadius: '0', minHeight: 'calc(100vh - 160px)' }}
                    title="SuperJarvis Dynamic View"
                  />
                )}
                {/* Pay Card overlay */}
                {payCardMandate && (
                  <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="w-full max-w-md">
                      <SuperJarvisPayCard
                        mandate={payCardMandate}
                        onApprove={handlePaymentApprove}
                        onDecline={handlePaymentDecline}
                        onUndo={handlePaymentUndo}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Conversation View — Standard centered layout (no canvas / DV2 fallback) */
            <div className="w-full max-w-4xl mx-auto flex flex-col space-y-5 px-4">
              {/* Sticky Chat Title */}
              <div className="sticky top-0 z-20 py-4 -mx-4 px-4 bg-gradient-to-b from-black via-black/80 to-transparent">
                <h2 className="text-center text-lg font-semibold text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] truncate">
                  {messages.find(m => m.role === 'user')?.content.substring(0, 50) || 'New Chat'}
                  {(messages.find(m => m.role === 'user')?.content.length || 0) > 50 ? '...' : ''}
                </h2>
              </div>

              {messages.map((message) => (
                <div
                  key={message.id}
                  data-testid={`chat-message-${message.role}`}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-center'}`}
                >
                  <div
                    className={`${message.role === 'user' ? 'max-w-[85%]' : 'w-full max-w-5xl'} px-5 py-4 rounded-2xl ${message.role === 'user'
                      ? 'bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 backdrop-blur-xl shadow-[0_0_15px_rgba(34,211,238,0.1)]'
                      : 'text-cyan-300'
                      }`}
                  >
                    {/* DynamicViewV2 fallback — composable block system (non-HTML recipes) */}
                    {message.role === 'assistant' && message.viewRecipe && 'blocks' in message.viewRecipe ? (
                      <DynamicViewV2
                        recipe={message.viewRecipe as ViewRecipe}
                        onAction={(prompt) => handleAction(prompt)}
                        onBookAction={(bookingData) => handleBookingAction(bookingData)}
                      />
                    ) : /* Check for DynamicView v1 format (Gemini-style adaptive UI) */
                      message.role === 'assistant' && parseDynamicView(message.content) ? (
                        <>
                          <div className="text-sm md:text-base leading-relaxed mb-4">
                            {processMarkdown(message.content.split(/\[DYNAMIC_VIEW:/)[0]?.trim() || '')}
                          </div>
                          <DynamicView
                            data={{
                              type: parseDynamicView(message.content)!.type,
                              items: parseDynamicView(message.content)!.items
                            }}
                            onSelect={(item) => handleAction(`Tell me more about ${item.title}`)}
                            onAction={(action) => action.prompt && handleAction(action.prompt)}
                          />
                        </>
                      ) : (
                        <div className="text-sm md:text-base leading-relaxed">{processMarkdown(message.content)}</div>
                      )}

                    {/* Sources button */}
                    {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                      <button
                        onClick={() => setSourcesPanel({ isOpen: true, sources: message.sources || [] })}
                        className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-xs text-cyan-400/70 hover:text-cyan-300 border border-cyan-400/20 hover:border-cyan-400/40 rounded-full transition-all hover:bg-cyan-400/5"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        {message.sources.length} Source{message.sources.length !== 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Streaming response or Loading indicator */}
              {isLoading && (
                <div data-testid="home-chat-loading" className="flex justify-center">
                  <div className="w-full max-w-5xl bg-cyan-500/[0.05] border border-cyan-400/20 px-5 py-4 rounded-2xl backdrop-blur-xl">
                    {streamingContent ? (
                      <div className="text-sm md:text-base leading-relaxed text-cyan-300">
                        {streamingContent}
                        <span className="inline-block w-2 h-4 bg-cyan-400 ml-1 animate-pulse" />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <div className="animate-pulse">
                          <AtomIcon className="w-6 h-6 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
                        </div>
                        <span className="text-cyan-400/60 text-sm">
                          {progressMessage || 'SuperJarvis is thinking...'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}


              {/* Pay Card inline for non-canvas mode */}
              {payCardMandate && (
                <div className="flex justify-center">
                  <div className="w-full max-w-md">
                    <SuperJarvisPayCard
                      mandate={payCardMandate}
                      onApprove={handlePaymentApprove}
                      onDecline={handlePaymentDecline}
                      onUndo={handlePaymentUndo}
                    />
                  </div>
                </div>
              )}

              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
            </div>
          )}

        </div>

        {/* Bottom Chat & Nav Section — FIXED at bottom, never moves */}
        <div className={`fixed bottom-0 left-0 z-30 px-4 pb-4 w-full ${inConversation ? 'pr-24' : ''}`}>
          <div className="flex flex-col items-center">
            <div className="w-full max-w-4xl mx-auto">
              <ChatBar
                inputText={inputText}
                setInputText={setInputText}
                chatMode={chatMode}
                setChatMode={setChatMode}
                onSend={handleChatSubmit}
                onGhostClick={openGhost}
                isLoading={isLoading}
              />
            </div>
            {!inConversation && <BottomNav />}
          </div>
          {inConversation && (
            <div className="fixed right-4 bottom-4 z-40">
              <SideNav />
            </div>
          )}
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }}
        userName={user?.displayName || user?.email?.split('@')[0]}
        userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />

      {/* Auth Modal - shown when unauthenticated users try to interact */}
      {/* Sources Panel - slides out from right */}
      <SourcesPanel
        isOpen={sourcesPanel.isOpen}
        onClose={() => setSourcesPanel({ isOpen: false, sources: [] })}
        sources={sourcesPanel.sources}
      />

      <AuthModal isOpen={authModalOpen} onClose={handleAuthModalClose} />

      {/* GuidedTour removed — redundant with IntentBubbles */}

      {/* Pulse Banner - shows value metrics at top */}
      <PulseBannerCompact />

      {/* Notification Center - proactive notifications from SuperJarvis */}
      <NotificationCenter
        position="top-right"
        maxNotifications={3}
      />
    </div>
  );
}
